import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Loader2 } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { Place, EmailData } from '../../../types/results.types';
import { BalanceResponse } from '../../../types/balance.types';
import { resolveRowState, findableCount, verifiableCount, readyCount } from '../../../utils/rowActions';
import { placeHasCatchAllEmails } from '../../../utils/catchAllDetection';
import { CatchAllBadge } from '../EmailBadge/EmailBadge';
import InlineBulkDMUnlockButton from '../InlineBulkDMUnlockButton/InlineBulkDMUnlockButton';
import InlineVerifyButton, { VerifyModalPayload } from '../InlineVerifyButton/InlineVerifyButton';
import InlineDMUnlockModal from '../InlineDMUnlockModal/InlineDMUnlockModal';
import EmailVerificationModal from '../EmailVerificationModal/EmailVerificationModal';
import { VERIFICATION_CREDITS_LABEL } from '../../../constants/creditTerms';
import HoverTooltip from '../../common/HoverTooltip/HoverTooltip';
import { buildEmailPreview, EmailPreviewResult } from './emailPreview';

const ROW_EMAIL_PILL_SHELL = 'inline-flex w-fit flex-col gap-1 max-w-full';

const ROW_CTA_BASE =
  'inline-flex w-fit items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all duration-[120ms] ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

const ROW_FIND_CTA_CLASS = `${ROW_CTA_BASE} bg-[#8A3033] text-white hover:bg-[#722629] active:scale-[0.98] shadow-sm focus-visible:outline-[#8A3033]`;

const ROW_VERIFY_CTA_SECONDARY_CLASS = `${ROW_CTA_BASE} border border-[#D97706] bg-white text-[#92400E] hover:bg-amber-50 active:scale-[0.98] focus-visible:outline-[#D97706]`;

const ROW_VERIFY_CTA_PRIMARY_CLASS = ROW_VERIFY_CTA_SECONDARY_CLASS;

const ROW_CTA_LOADING_CLASS = `${ROW_CTA_BASE} bg-gray-100 text-gray-400 cursor-not-allowed shadow-none`;

const ROW_LOCKED_CTA_CLASS = `${ROW_CTA_BASE} bg-gray-400 text-white hover:bg-gray-500 shadow-sm focus-visible:outline-gray-400`;

const ROW_GATED_UPGRADE_PILL_CLASS =
  'inline-flex w-fit items-center gap-1.5 rounded-full border border-[#E6D7D7] bg-white px-3 py-1 text-xs font-semibold text-[#B46A6D] shadow-none transition-colors hover:border-[#C98B8E] hover:bg-[#FBF7F7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C98B8E]';

const ROW_READY_STATUS_CLASS =
  'inline-flex w-fit items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#E1F5EE] text-[#0F6E56]';

const ROW_CREDIT_HINT_CLASS = 'text-[10px] text-[#9CA3AF] leading-tight';

const ROW_PREVIEW_ACTIONS_CLASS = 'mt-1.5 flex w-fit flex-col gap-1';

type ActiveRowModal = 'find' | 'verify' | null;

const AUTO_NO_EMAIL_REASON_COPY: Record<string, { label: string; tooltip: string }> = {
  FAILED_NO_WEBSITE: {
    label: 'no website found',
    tooltip: 'This business has no website we could check. We did not charge you.',
  },
  FAILED_NO_PATTERNS: {
    label: 'email format unknown',
    tooltip: 'We found the website but could not work out their email format. You were not charged.',
  },
  CATCH_ALL: {
    label: 'could not confirm',
    tooltip: 'This domain accepts any address, so we could not confirm a specific one. You were not charged.',
  },
};

const DEFAULT_AUTO_NO_EMAIL_COPY = {
  label: 'no email found',
  tooltip: 'We looked but could not find a confirmed email. You were not charged.',
};

interface AutoFindAnalyticsProps {
  autoFindMode?: string | null;
  autoFindSuccessLimit?: number;
  autoFindSuccessCount?: number;
  autoFindAttemptCount?: number;
  autoFindNoEmailCount?: number;
  autoFindCatchAllCount?: number;
  autoFindGuardrailReason?: string | null;
}

interface EmailStatusCellProps extends AutoFindAnalyticsProps {
  place: Place;
  balance: BalanceResponse | null;
  searchId?: number;
  currentPlan?: string | null;
  onViewDetails: () => void;
  onUpgradeClick?: () => void;
}

const EmailStatusCell: React.FC<EmailStatusCellProps> = ({
  place,
  balance: _balance,
  searchId,
  currentPlan,
  autoFindMode,
  autoFindSuccessLimit,
  autoFindSuccessCount,
  autoFindAttemptCount,
  autoFindNoEmailCount,
  autoFindCatchAllCount,
  autoFindGuardrailReason,
  onViewDetails: _onViewDetails,
  onUpgradeClick,
}) => {
  const state = resolveRowState(place);
  const posthog = usePostHog();
  const gatedViewedRef = useRef(false);

  // Snapshots captured at open time, not re-derived from live `place` during render.
  // This ensures a results refresh cannot mutate the payload or unmount an active modal.
  const [activeModal, setActiveModal] = useState<ActiveRowModal>(null);
  const [modalPersonIds, setModalPersonIds] = useState<number[]>([]);
  const [modalPersonEmails, setModalPersonEmails] = useState<EmailData[]>([]);
  const [modalPlaceEmails, setModalPlaceEmails] = useState<EmailData[]>([]);
  const [modalBusinessName, setModalBusinessName] = useState<string | undefined>();
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  const openFindModal = (personIds: number[]) => {
    if (!personIds.length) return;
    setModalPersonIds(personIds);
    setActiveModal('find');
  };

  const closeFindModal = () => {
    setActiveModal(null);
    setModalPersonIds([]);
  };

  const openVerifyModal = ({ personEmails, placeEmails, businessName }: VerifyModalPayload) => {
    setModalPersonEmails(personEmails);
    setModalPlaceEmails(placeEmails);
    setModalBusinessName(businessName);
    setActiveModal('verify');
  };

  const closeVerifyModal = () => {
    setActiveModal(null);
    setModalPersonEmails([]);
    setModalPlaceEmails([]);
    setModalBusinessName(undefined);
  };

  const fCount = findableCount(place);
  const vCount = verifiableCount(place);
  const readyEmailsCount = readyCount(place);
  const emailPreview = buildEmailPreview(place);
  const hasFinding = (place.people ?? []).some(p => p.email_access_state === 'finding');
  const hasAutoNoEmail = (place.people ?? []).some(p => p.email_access_state === 'auto_no_email');
  const hasGatedPeople = (place.people ?? []).some(p => p.email_access_state === 'gated_upgrade');
  const gatedPeopleCount = (place.people ?? []).filter(p => p.email_access_state === 'gated_upgrade').length;
  const autoCheckedCount = (place.people ?? []).filter(
    p => p.email_access_state === 'auto_no_email' || p.email_access_state === 'auto_found'
  ).length;
  const autoNoEmailPerson = (place.people ?? []).find((person) => person.email_access_state === 'auto_no_email');
  const autoNoEmailCopy = autoNoEmailPerson?.unlock_type
    ? (AUTO_NO_EMAIL_REASON_COPY[autoNoEmailPerson.unlock_type] ?? DEFAULT_AUTO_NO_EMAIL_COPY)
    : DEFAULT_AUTO_NO_EMAIL_COPY;
  const hasAutoFound = (place.people ?? []).some((person) => person.email_access_state === 'auto_found');
  const shouldShowGate = readyEmailsCount === 0 && (
    emailPreview.totalCount === 0 &&
    (place.email_action_gated === true || hasGatedPeople)
  );
  const gateReason = place.email_gate_reason || 'discover_remaining_lead';

  const autoFindEventProps = {
    auto_find_mode: autoFindMode ?? 'discover_auto',
    success_limit: autoFindSuccessLimit ?? 0,
    success_count: autoFindSuccessCount ?? 0,
    attempt_count: autoFindAttemptCount ?? 0,
    no_email_count: autoFindNoEmailCount ?? 0,
    catch_all_count: autoFindCatchAllCount ?? 0,
    gate_reason: autoFindGuardrailReason ?? gateReason,
    purchased_dm_spent: 0,
  };

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email).then(() => {
      setCopiedEmail(email);
      window.setTimeout(() => setCopiedEmail((current) => current === email ? null : current), 1200);
    });
  };

  const renderEmailPreview = (preview: EmailPreviewResult, actions?: React.ReactNode) => {
    return (
      <div className={ROW_EMAIL_PILL_SHELL} onClick={(event) => event.stopPropagation()}>
        <div className="flex min-w-0 flex-col gap-0.5 leading-tight">
          {preview.visible.map((item, index) => {
            const isCopied = copiedEmail === item.email.email;
            return (
              <span
                key={`${item.source}-${item.email.id ?? item.email.email}`}
                className="group/email inline-flex max-w-[230px] items-center gap-1"
              >
                <span
                  className={`min-w-0 truncate font-mono ${
                    index === 0
                      ? 'text-[12.5px] font-semibold text-[#0F6E56]'
                      : item.isGeneric
                        ? 'text-[10.5px] font-medium text-[#86B8A8]'
                        : 'text-[11px] font-medium text-[#059669]'
                  }`}
                  title={item.email.email}
                >
                  {item.email.email}
                </span>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCopyEmail(item.email.email);
                  }}
                  className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded text-[#9CA3AF] opacity-0 transition-opacity hover:bg-[#EEF7F3] hover:text-[#0F6E56] focus-visible:opacity-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-[#0F6E56] group-hover/email:opacity-100"
                  title={isCopied ? 'Copied' : 'Copy email'}
                  aria-label={isCopied ? 'Copied' : `Copy ${item.email.email}`}
                >
                  {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </button>
              </span>
            );
          })}
          {preview.hiddenCount > 0 && (
            <span className="mt-0.5 text-[10.5px] font-medium text-[#9CA3AF]">
              +{preview.hiddenCount} more
            </span>
          )}
        </div>
        {actions && <div className={ROW_PREVIEW_ACTIONS_CLASS}>{actions}</div>}
      </div>
    );
  };

  const renderFindAction = (showHint = true) => (
    <>
      <InlineBulkDMUnlockButton
        place={place}
        searchId={searchId}
        onUpgradeClick={onUpgradeClick}
        onOpenModal={openFindModal}
        buttonClassName={ROW_FIND_CTA_CLASS}
        lockedButtonClassName={ROW_LOCKED_CTA_CLASS}
        loadingButtonClassName={ROW_CTA_LOADING_CLASS}
      />
      {showHint && fCount > 0 && (
        <p className={ROW_CREDIT_HINT_CLASS}>
          Free if we don't find one.
        </p>
      )}
    </>
  );

  const renderVerifyAction = (buttonClassName = ROW_VERIFY_CTA_PRIMARY_CLASS, showHint = true) => (
    <>
      <InlineVerifyButton
        place={place}
        searchId={searchId}
        onUpgradeClick={onUpgradeClick}
        onOpenModal={openVerifyModal}
        buttonClassName={buttonClassName}
        lockedButtonClassName={ROW_LOCKED_CTA_CLASS}
        loadingButtonClassName={ROW_CTA_LOADING_CLASS}
      />
      {showHint && vCount > 0 && (
        <p className={ROW_CREDIT_HINT_CLASS}>
          {vCount} {vCount === 1 ? 'verification credit' : VERIFICATION_CREDITS_LABEL}
        </p>
      )}
    </>
  );

  useEffect(() => {
    if (!shouldShowGate || gatedViewedRef.current) return;
    gatedViewedRef.current = true;
    posthog?.capture('gated_email_action_viewed', {
      search_id: searchId,
      current_plan: 'discover',
      surface: 'results_table',
      ...autoFindEventProps,
    });
  }, [gateReason, posthog, searchId, shouldShowGate]);

  let content: React.ReactNode;

  if (emailPreview.totalCount > 0) {
    const actions = state === 'both'
      ? (
          <>
            {renderFindAction(false)}
            {renderVerifyAction(ROW_VERIFY_CTA_SECONDARY_CLASS, false)}
          </>
        )
      : state === 'find'
        ? renderFindAction(false)
        : state === 'verify'
          ? renderVerifyAction(ROW_VERIFY_CTA_PRIMARY_CLASS, false)
          : null;

    content = renderEmailPreview(emailPreview, actions);
  } else if (hasFinding && readyEmailsCount === 0) {
    content = (
      <span className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-[#6B7280]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Looking...
      </span>
    );
  } else if (shouldShowGate) {
    content = (
      <button
        type="button"
        className={ROW_GATED_UPGRADE_PILL_CLASS}
        onClick={(event) => {
          event.stopPropagation();
          posthog?.capture('gated_email_action_clicked', {
            search_id: searchId,
            current_plan: 'discover',
            surface: 'results_table',
            ...autoFindEventProps,
          });
          onUpgradeClick?.();
        }}
      >
        <span>{autoCheckedCount > 0 && gatedPeopleCount > 0 ? `Check remaining ${gatedPeopleCount} ${gatedPeopleCount === 1 ? 'lead' : 'leads'}` : 'Find email'}</span>
        <span className="text-[#B46A6D]">· Upgrade</span>
      </button>
    );
  } else if (hasAutoNoEmail && readyEmailsCount === 0 && placeHasCatchAllEmails(place)) {
    content = (
      <HoverTooltip content={autoNoEmailCopy.tooltip} wrapperClassName="inline-flex">
        <div className={ROW_EMAIL_PILL_SHELL}>
          <CatchAllBadge tone="subtle" />
          <span className="text-xs font-medium text-[#9CA3AF]">{autoNoEmailCopy.label}</span>
        </div>
      </HoverTooltip>
    );
  } else if (hasAutoNoEmail && readyEmailsCount === 0) {
    content = (
      <HoverTooltip content={autoNoEmailCopy.tooltip} wrapperClassName="inline-flex">
        <span className="inline-flex w-fit flex-col text-xs leading-tight text-[#9CA3AF]">
          <span className="font-medium">{autoNoEmailCopy.label}</span>
        </span>
      </HoverTooltip>
    );
  } else switch (state) {
    case 'empty':
      content = placeHasCatchAllEmails(place) ? (
        <div className={ROW_EMAIL_PILL_SHELL}>
          <CatchAllBadge tone="subtle" />
        </div>
      ) : (
        <span className="text-[12px] text-[#D1D5DB]">-</span>
      );
      break;

    case 'generic_only':
      content = (
        <div className={ROW_EMAIL_PILL_SHELL}>
          <span className="text-[10.5px] text-gray-400 italic">company email</span>
          {placeHasCatchAllEmails(place) && <CatchAllBadge tone="subtle" />}
        </div>
      );
      break;

    case 'ready': {
      content = (
        <span
          className={ROW_READY_STATUS_CLASS}
          title={hasAutoFound ? 'Found and confirmed. This used 1 lookup.' : undefined}
        >
          {readyEmailsCount} ready to contact
        </span>
      );
      break;
    }

    case 'find':
      content = (
        <div className={`${ROW_EMAIL_PILL_SHELL} group`} onClick={(e) => e.stopPropagation()}>
          {renderFindAction()}
        </div>
      );
      break;

    case 'verify':
      if (vCount === 0 && placeHasCatchAllEmails(place)) {
        content = (
          <div className={ROW_EMAIL_PILL_SHELL} onClick={(e) => e.stopPropagation()}>
            <CatchAllBadge tone="subtle" />
            <button
              type="button"
              onClick={_onViewDetails}
              className="text-[10.5px] text-gray-400 underline hover:text-gray-600"
            >
              View emails
            </button>
          </div>
        );
      } else {
        content = (
          <div className={`${ROW_EMAIL_PILL_SHELL} group`} onClick={(e) => e.stopPropagation()}>
            {renderVerifyAction()}
          </div>
        );
      }
      break;

    default: // 'both'
      content = (
        <div className={`${ROW_EMAIL_PILL_SHELL} group`} onClick={(e) => e.stopPropagation()}>
          {renderFindAction()}
          {renderVerifyAction(ROW_VERIFY_CTA_SECONDARY_CLASS)}
        </div>
      );
  }

  return (
    <>
      {content}
      <InlineDMUnlockModal
        isOpen={activeModal === 'find' && modalPersonIds.length > 0}
        onClose={closeFindModal}
        personIds={modalPersonIds}
        searchId={searchId}
        onUpgradeClick={onUpgradeClick}
      />
      {activeModal === 'verify' && (
        <EmailVerificationModal
          isOpen
          onClose={closeVerifyModal}
          personEmails={modalPersonEmails}
          placeEmails={modalPlaceEmails}
          emailType="both"
          searchId={searchId}
          bulkMode={false}
          businessName={modalBusinessName}
          skipInitialSelection
        />
      )}
    </>
  );
};

export default EmailStatusCell;
